import { useTranslation } from '@jupiter/plugin-runtime/i18n';
import cx from 'classnames';
import React, { useEffect, useState } from 'react';

import {
  NewAndUpdatedNotification,
  setNewAndUpdatedNotificationStorage,
} from '@/components/new-and-updated-notification/new-and-updated-notification';

import {
  LearningInfoWithTagInfo,
  LearningType,
  sellerLearningCenterApiClient,
} from '@/api/seller/serv/oec_seller_learning_center_api';
import { CODE_SUCCESS } from '@features/constants/network';
import styles from './new-and-updated-section.scss';

import { ReactComponent as NewIcon } from '@/assets/imgs/new-label-background.svg';
import { ReactComponent as UpdatedLabel } from '@/assets/imgs/updated-label-background.svg';

interface NewAndUpdatedSectionProps {
  className?: string;
}

interface StatusLabel {
  Icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  text: string;
}

export const NewAndUpdatedSection = ({
  className,
}: NewAndUpdatedSectionProps) => {
  const { i18n, t } = useTranslation();
  const [moduleArr, setModuleArr] = useState<
    [NewAndUpdatedModule, NewAndUpdatedModule] | []
  >([]);
  const [isError, setIsError] = useState(false);
  const [isHighlighted, setIsHighlighted] = useState(false);
  const history = useHistory();

  useEffect(() => {
    if (history.location.search.includes('from=new-and-updated-notification')) {
      setIsHighlighted(true);
    }
    fetchNewAndUpdatedArticles()
      .catch(() => setIsError(true))
      .then(apiResponse => {
        if (!apiResponse) {
          return;
        }
        const modules = [
          {
            name: t('academy_latest_courses_articles'),
            module_name: 'latest_courses_articles',
            seller_learning_info: apiResponse.sellerCourses,
          },
          {
            name: t('academy_latest_policies'),
            module_name: 'latest_policies',
            seller_learning_info: apiResponse.sellerPolicies,
          },
        ] as [NewAndUpdatedModule, NewAndUpdatedModule];
        setModuleArr(modules);
      });
  }, [i18n.language, history.location]);
  const showModule = moduleArr?.length > 0;

  const shouldShowModule = showModule && moduleArr?.length > 1;

  const classes = [styles.container, `grid grid-cols-2 gap-16`, className];
  if (isHighlighted) {
    classes.push(styles.highlighted);
  }
  const classNames = cx(...classes);

  return !isError ? (
    <section
      id="new-and-updated"
      className={classNames}
      style={{ width: '80rem', margin: '64px auto 0' }}>
      {shouldShowModule ? (
        <>
          <NewAndUpdatedNotification />
          {moduleArr.map((module: NewAndUpdatedModule) => (
            <NewAndUpdatedList
              key={module.module_name}
              configId={module.module_name}
              title={module.name}
              creatorArticleList={module.creator_learning_info}
              sellerArticleList={module.seller_learning_info}
            />
          ))}
        </>
      ) : (
        <>
          <NewAndUpdatedSectionSkeleton /> <NewAndUpdatedSectionSkeleton />
        </>
      )}
    </section>
  ) : null;
};

interface NewAndUpdatedModule {
  name?: string;
  module_name?: string;
  seller_learning_info?: Array<LearningInfoWithTagInfo>;
  creator_learning_info?: Array<LearningInfoWithTagInfo>;
}

const fetchNewAndUpdatedArticles = async () => {
  // Fetch both seller and creator content in parallel
  // perfNote(Max): this should be one API call
  // TODO(Max): use Promise.allSettled()
  const responses = await Promise.all([
    sellerLearningCenterApiClient.AcademyNewAndUpdatedContents({
      user_type: 1,
    }),
  ]);
  const didErr = responses.find(res => res.code !== CODE_SUCCESS);
  if (didErr) {
    throw new Error(
      'Failed to fetch new and updated articles: ' + didErr?.code,
    );
  }
  const [sellerRes] = responses;

  const filterValidData = (data?: LearningInfoWithTagInfo[]) =>
    (data ?? []).filter(item => item.learning_info?.learning_id !== 0);

  const sellerCourses =
    sellerRes.code === CODE_SUCCESS
      ? (filterValidData(sellerRes.course_feature_guide) ?? [])
      : [];

  const sellerPolicies =
    sellerRes.code === CODE_SUCCESS
      ? (filterValidData(sellerRes.policy) ?? [])
      : [];

  return { sellerCourses, sellerPolicies };
};

import { Tabs } from '@m4b-design/components';
import { useHistory } from 'react-router-dom';

import LearnMore from '@/components/learn-more';
import { Path } from '@/constants';
import { IconCheckCircleFill } from '@arco-design/iconbox-react-m4b-next';
import { Tea } from '@features/libs/tea';
import { PagePathV2 } from '@features/types';

interface ModuleContainerProps {
  configId?: string;
  title?: string;
  creatorArticleList?: LearningInfoWithTagInfo[];
  sellerArticleList?: LearningInfoWithTagInfo[];
}

const NewAndUpdatedList: React.FC<ModuleContainerProps> = props => {
  const { t } = useTranslation();
  const {
    title,
    creatorArticleList = [],
    sellerArticleList = [],
    configId,
  } = props;

  const history = useHistory();

  const getCourseUrl = (article: LearningInfoWithTagInfo) => {
    const learningId = article.learning_info?.learning_id;
    // See NOTE[basename-is-weird]
    const basename: '/university' = history
      .createHref(new URL('https://tiktok.com'))
      .slice(0, -1) as '/university';
    const pathname =
      article.learning_info?.learning_type === LearningType.Course
        ? PagePathV2.CourseDetail
        : PagePathV2.Essay;
    const newSearchParams = new URLSearchParams(window.location.search);
    newSearchParams.set('from', 'new-and-updated');
    newSearchParams.set('knowledge_id', String(learningId));
    newSearchParams.set('learning_id', String(learningId));
    return `${basename}${pathname}?${newSearchParams.toString()}` as const;
  };

  const getSeeAllUrl = (moduleId: string | undefined) => {
    const newSearchParams = new URLSearchParams(window.location.search);
    newSearchParams.set('from', 'new-and-updated');
    newSearchParams.set('module_id', String(moduleId));
    newSearchParams.delete('page');
    let path: (typeof Path)[keyof typeof Path] = Path.NewArticles;
    if (moduleId === 'latest_policies') {
      path = Path.NewPolicies;
    }
    // See NOTE[basename-is-weird]
    const basename: '/university' = history
      .createHref(new URL('https://tiktok.com'))
      .slice(0, -1) as '/university';
    return `${basename}${path}?${newSearchParams.toString()}#main` as const;
  };

  // Check if we have articles for sellers or creators
  const hasSellerArticles = sellerArticleList.length > 0;
  const hasCreatorArticles = creatorArticleList.length > 0;

  const makeLinkToArticle = (
    article: LearningInfoWithTagInfo,
    index: number,
  ) => {
    const url = getCourseUrl(article);
    let IconToUse: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
    const isRead = article.user_seen_tag;
    if (article.new_or_updated_tag === 1 && !isRead) {
      IconToUse = NewIcon;
    } else if (article.new_or_updated_tag === 2 && !isRead) {
      IconToUse = UpdatedLabel;
    } else {
      IconToUse = () => null;
    }
    const { i18n } = useTranslation();
    const localeCode = i18n.language;
    const createdAtUnix = article.learning_info!.create_time;
    const updatedAtUnix = article.learning_info!.modify_time;
    const createdAt = createdAtUnix ? new Date(createdAtUnix) : null;
    const createdAtH =
      article.new_or_updated_tag === 1 && createdAt ? (
        <time dateTime={createdAt.toISOString().split('T')[0]}>
          {t('academy_latest_publish_date', {
            placeholder_date: createdAt.toLocaleDateString(localeCode, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            }),
          })}
        </time>
      ) : null;
    const updatedAt = updatedAtUnix ? new Date(updatedAtUnix) : null;
    const updatedAtH =
      article.new_or_updated_tag === 2 && updatedAt ? (
        <time dateTime={updatedAt.toISOString().split('T')[0]}>
          {t('academy_latest_date', {
            placeholder_date: updatedAt.toLocaleDateString(localeCode, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            }),
          })}
        </time>
      ) : null;

    const readH = isRead ? (
      <span>
        <span className={styles.dot}>·</span>
        <span>
          <IconCheckCircleFill /> Read
        </span>
      </span>
    ) : null;

    const getStatusLabel = (): StatusLabel | null => {
      if (article.new_or_updated_tag === 1 && !isRead) {
        return {
          Icon: NewIcon,
          text: t('academy_latest_new_tag'),
        };
      } else if (article.new_or_updated_tag === 2 && !isRead) {
        return {
          Icon: UpdatedLabel,
          text: t('academy_latest_updated_tag'),
        };
      }
      return null;
    };

    const article_status = getStatusLabel();

    return (
      <li key={article.learning_info!.learning_id}>
        <div>
          {article_status && (
            <div className={styles.statusWrapper}>
              <IconToUse className={styles.statusIcon} />
              <span className={styles.statusText}>{article_status.text}</span>
            </div>
          )}{' '}
          <a
            href={url}
            onClick={e => {
              reportModuleContentClickEvent(
                title,
                String(article.learning_info?.learning_id),
                String(index),
              );
              setNewAndUpdatedNotificationStorage({ hasVisitedNRU: true });
              // respect holding modifier keys to open in new tab
              if (e.ctrlKey || e.shiftKey || e.altKey || e.metaKey) return;
              e.preventDefault();

              // NOTE[basename-is-weird]: react-router is dumb and
              // its history APIs are all relative to the "basename"
              // (which will always be `/university/`) while normal
              // URLs are relative to the origin. Therefore when supporting both
              // soft and hard navs, we need to pick the correct URL to use
              const basename: '/university' = history
                .createHref(new URL('https://tiktok.com'))
                .slice(0, -1) as '/university';
              const hrefWithStrippedBasename = url!.replace(basename, '');
              history.push(hrefWithStrippedBasename);
            }}>
            {article.learning_info!.name}
          </a>
        </div>
        <p>
          {updatedAtH ?? createdAtH}
          {readH}
        </p>
      </li>
    );
  };

  const numArticlesToShow = 4;
  const sellerArticles = sellerArticleList
    .filter(article => !!article.learning_info?.learning_id)
    .slice(0, numArticlesToShow)
    .map(makeLinkToArticle);

  const creatorArticles = creatorArticleList
    .filter(article => !!article.learning_info?.learning_id)
    .slice(0, numArticlesToShow)
    .map(makeLinkToArticle);

  const defaultActiveTab = window.location.search.includes('tab=creators')
    ? 'creators'
    : 'sellers';
  const [activeTab, setActiveTab] = React.useState(defaultActiveTab);
  const updateActiveTab = (key: string) => {
    setActiveTab(key);
    const url = new URL(window.location.href);
    if (key === 'sellers') {
      url.searchParams.set('tab', 'sellers');
    } else if (key === 'creators') {
      url.searchParams.set('tab', 'creators');
    }
    window.history.replaceState({}, '', url.toString());
  };

  return (
    <section className={styles.tab}>
      <header>
        <h1>{title}</h1>
        <LearnMore
          classNames="whitespace-nowrap"
          anal={() => reportLearnMoreClickEvent(title)}
          href={getSeeAllUrl(configId)}
          textKey="academy_latest_see_all"
        />
      </header>
      <div className={styles.tabContainer}>
        {/* Show Tabs only if both seller and creator articles exist */}
        {hasSellerArticles && hasCreatorArticles ? (
          <Tabs activeTab={activeTab} onChange={updateActiveTab}>
            <Tabs.TabPane key="sellers" title="For Sellers">
              <ul>{sellerArticles}</ul>
            </Tabs.TabPane>
            <Tabs.TabPane key="creators" title="For Creators">
              <ul>{creatorArticles}</ul>
            </Tabs.TabPane>
          </Tabs>
        ) : hasSellerArticles ? (
          // Show only Seller articles without tabs
          <ul>{sellerArticles}</ul>
        ) : hasCreatorArticles ? (
          // Show only Creator articles without tabs
          <ul>{creatorArticles}</ul>
        ) : null}
      </div>
    </section>
  );
};

const reportLearnMoreClickEvent = (moduleName: string | undefined) => {
  Tea('button_click', {
    page_name: 'seller_university_homepage',
    tab_name: 'home_config_module_entrance',
    click_for: 'learn_more',
    module_name: moduleName,
  });
};

const reportModuleContentClickEvent = (
  moduleName: string | undefined,
  contentId: string,
  index: string,
) => {
  Tea('button_click', {
    page_name: 'seller_university_homepage',
    tab_name: 'home_config_module_entrance',
    click_for: 'content',
    module_name: moduleName,
    content_id: contentId,
    index: index,
  });
};

const NewAndUpdatedSectionSkeleton = () => (
  <div className={styles.skeletonContainer}>
    <header>
      <p className={styles.headerTitle}></p>
      <p className={styles.headerSubtitle}></p>
      <p className={styles.headerSubtitle}></p>
    </header>

    <div className={styles.skeletonContent}>
      <p className={styles.contentLineFull}></p>
      <p className={styles.contentLineShort}></p>

      <p className={styles.contentLineFull}></p>
      <p className={styles.contentLineShort}></p>

      <p className={styles.contentLineFull}></p>
      <p className={styles.contentLineShort}></p>

      <p className={styles.contentLineFull}></p>
      <p className={styles.contentLineShort}></p>
    </div>
  </div>
);
